import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import {
  selectAuth,
  selectUserRoles,
} from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROLE_CLIENT_ADMIN,
  ROLE_SUPER_ADMIN,
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import {
  IsLoadedKeyContacts,
  IsLoadingKeyContacts,
  loadKeyContacts,
  SelectActualCompanyOwnerUserId,
  SelectActualKeyContactData,
  SelectActualRelationshipReviewId,
  SelectContact,
  SelectIsLoadingActualKeyContactData,
  SelectOrderedByContacts,
  setActualKeyContactData,
  setActualRelationshipReviewId,
  setIsLoadingActualKeyContactData,
  setIsLoadingKeyContacts,
} from "../redux/reducers/clientKeyContacts.reducer";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  SelectOrderedBy,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client, ClientsKeyContactDetail } from "../types/clients-module-types";
import { SelectUsers } from "../../BillingModule/redux/reducers/metaBilling.reducer";
import {
  IsReviewsLoaded,
  loadCostCentersOwned,
  loadReviewsInfo,
  SelectCostCenterOwnedIds,
  SelectOrderedByReviewsList,
  setIsLoadingReviews,
} from "../redux/reducers/clientRelationshipReviewsList.reducer";
import { UsersService } from "../../UsersModule/services/users";
import {
  isSupervisor,
  verifyModuleAccess,
} from "../../SharedModule/utils/accessUtils";
import { SelectCostCentersOwned } from "../../UsersModule/redux/reducers/usersList.reducer";

import { useClientsList } from "./useClientsList";

const loadHeaderInfoIfNeeded = async (
  clientId,
  clientForHeaderInfo,
  dispatch,
  navigate
) => {
  if (clientForHeaderInfo && clientForHeaderInfo.id === clientId) {
    return;
  }
  dispatch(setIsActualClientForHeaderInfoLoading(true));
  const headerInfo = await ClientsService.getClientModuleHeaderInfo(clientId)
    .then((response) => {
      if (!response.success) {
        dispatch(
          addToast({
            mode: "error",
            message: response.message,
          })
        );
        dispatch(setIsActualClientForHeaderInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return response.data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting the client header information.",
        })
      );
      dispatch(setIsActualClientForHeaderInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  dispatch(setActualClientForHeaderInfo(headerInfo));
};

const loadCostCentersIfSupervisor = async (
  isSupervisorInfo,
  notSuperOrClientAdmin,
  costCentersOwned,
  activeUserEmail,
  dispatch
) => {
  if (!isSupervisorInfo || !notSuperOrClientAdmin || costCentersOwned) {
    return;
  }
  let costCentersOwnedResponse;
  if (!costCentersOwned) {
    costCentersOwnedResponse = await UsersService.getCostCentersOwned(
      activeUserEmail
    );
  }
  dispatch(
    loadCostCentersOwned({
      costCentersOwned: costCentersOwned || costCentersOwnedResponse,
      applySupervisorCheck: true,
    })
  );
};

const loadReviewsIfNeeded = async (reviewsLoaded, clients, dispatch) => {
  if (reviewsLoaded) {
    return;
  }
  dispatch(setIsLoadingReviews(true));
  const reviewsInfo = await ClientsService.getClientRelationshipReviews();
  dispatch(loadReviewsInfo({ data: reviewsInfo.data, clients }));
};

const loadKeyContactsIfNeeded = async (
  isLoadedKeyContacts,
  actualRelationshipReviewId,
  relationshipReviewId,
  dispatch,
  navigate
) => {
  if (
    isLoadedKeyContacts &&
    actualRelationshipReviewId &&
    actualRelationshipReviewId === relationshipReviewId
  ) {
    return;
  }
  dispatch(setIsLoadingKeyContacts(true));
  dispatch(setActualRelationshipReviewId(relationshipReviewId));
  const keyContactsInfo = await ClientsService.getClientKeyContacts(
    relationshipReviewId
  )
    .then((data) => data)
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Key Contacts not found",
        })
      );
      dispatch(setIsLoadingKeyContacts(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  dispatch(loadKeyContacts(keyContactsInfo));
};

export const useClientKeyContactDetails = (
  clientId: number,
  relationshipReviewId: number,
  keyContactId: number | null,
  routeBack: string
) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list: keyContacts } = useSelector(SelectOrderedByContacts);
  const isLoadingKeyContacts = useSelector(IsLoadingKeyContacts);
  const isLoadedKeyContacts = useSelector(IsLoadedKeyContacts);
  const {
    userRolesList,
    rolesLoaded,
    activeUserId,
    activeUserName,
    activeUserEmail,
  } = useSelector(selectUserRoles);
  const contact = useSelector(SelectContact);

  const actualKeyContactData: ClientsKeyContactDetail = useSelector(
    SelectActualKeyContactData
  );
  const isLoadingActualKeyContactData = useSelector(
    SelectIsLoadingActualKeyContactData
  );
  const auth = useSelector(selectAuth);
  const users = useSelector(SelectUsers);

  const today: Date = new Date();
  today.setHours(0);
  today.setMinutes(0);
  today.setSeconds(0);

  const trendingOptions = [
    { value: 1, label: "Up" },
    { value: 2, label: "Down" },
    { value: 3, label: "Same" },
  ];

  const actualRelationshipReviewId = useSelector(
    SelectActualRelationshipReviewId
  );

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);
  const { clientlist: clients } = useSelector(SelectOrderedBy);

  const costCentersOwned = useSelector(SelectCostCentersOwned);
  const costCenterOwnedIds = useSelector(SelectCostCenterOwnedIds);
  const isSupervisorInfo = isSupervisor(userRolesList);
  const notSuperOrClientAdmin = !verifyModuleAccess(
    true,
    [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
    userRolesList
  );

  const actualCompanyOwnerUserId = useSelector(SelectActualCompanyOwnerUserId);

  const reviewsLoaded = useSelector(IsReviewsLoaded);
  const { reviewsList: clientRelationshipReview } = useSelector(
    SelectOrderedByReviewsList
  );

  const loadClientKeyContacts = useCallback(
    async (clientId) => {
      if (!rolesLoaded || !isClientsLoaded) {
        return;
      }
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Clients Module Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      // Load info header when user put a new URL
      await loadHeaderInfoIfNeeded(
        clientId,
        ClientForHeaderInfo,
        dispatch,
        navigate
      );
      // Load info when user is supervisor
      await loadCostCentersIfSupervisor(
        isSupervisorInfo,
        notSuperOrClientAdmin,
        costCentersOwned,
        activeUserEmail,
        dispatch
      );
      // need relationships to get costCenterId to check is user could add key contact
      await loadReviewsIfNeeded(reviewsLoaded, clients, dispatch);
      // key contacts info
      await loadKeyContactsIfNeeded(
        isLoadedKeyContacts,
        actualRelationshipReviewId,
        relationshipReviewId,
        dispatch,
        navigate
      );
    },
    [
      dispatch,
      rolesLoaded,
      isClientsLoaded,
      ClientForHeaderInfo,
      isLoadedKeyContacts,
    ]
  );

  const loadKeyContactDetails = useCallback(
    async (id) => {
      // page info
      if (
        rolesLoaded &&
        isClientsLoaded &&
        ClientForHeaderInfo &&
        isLoadedKeyContacts &&
        id &&
        clientsIds.includes(clientId)
      ) {
        let keyContactsIds = keyContacts.map((kc) => kc.id);
        if (!keyContactsIds.includes(id)) {
          dispatch(
            addToast({
              mode: "error",
              message: "This key contact is not in this relationship review",
            })
          );
          navigate(ROUTE_CLIENTS);
          return true;
        }
        if (!isLoadingActualKeyContactData)
          dispatch(setIsLoadingActualKeyContactData(true));
        const response = await ClientsService.getKeyContactDetails(id)
          .then((response) => {
            if (!response.success) {
              dispatch(
                addToast({
                  mode: "error",
                  message: "Contact not found",
                })
              );
              dispatch(setIsLoadingActualKeyContactData(false));
              navigate(ROUTE_FORBIDDEN_PAGE);
              return true;
            } else {
              return response.data;
            }
          })
          .catch(() => {
            dispatch(
              addToast({
                mode: "error",
                message:
                  "Something wrong happened while getting the key contact details info.",
              })
            );
            navigate(routeBack);
          });
        dispatch(setActualKeyContactData(response));
      }
    },
    [
      dispatch,
      rolesLoaded,
      isClientsLoaded,
      ClientForHeaderInfo,
      isLoadedKeyContacts,
    ]
  );

  useEffect(() => {
    loadClientKeyContacts(clientId);
    loadKeyContactDetails(keyContactId);
  }, [loadClientKeyContacts]);

  return {
    ClientForHeaderInfo,
    auth,
    activeUserId,
    activeUserName,
    users,
    isLoadingKeyContacts,
    keyContacts,
    userRolesList,
    rolesLoaded,
    today,
    trendingOptions,
    contact,
    actualKeyContactData,
    isLoadingActualKeyContactData,
    loadKeyContactDetails,
    setIsLoadingActualKeyContactData,
    costCenterOwnedIds,
    actualCompanyOwnerUserId,
    clientRelationshipReview,
  };
};
